import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, User, UserAccessLevel } from '../services/authService';
import { termsConditionsService } from '../services/termsConditionsService';
import {
  PortalBrand,
  BrandMismatchError,
  applyPortalBrandToDocument,
  assignmentsMatchEntryBrand,
  clearPersistedPortalBrand,
  filterAssignmentsForEntryBrand,
  getBrandFromHostname,
  getLegalDocumentBrand,
  isStaffAllowedOnBrand,
  isTalAcquisitionCustomer,
  persistPortalBrand,
} from '../utils/branding';

export interface UserAssignment {
  id: string;
  red_rock_id: string;
  red_rock_full_entity_name: string;
  user_access_type: 'can_view' | 'can_edit';
  assigned_date: string;
  lead_source?: string | null;
  lead_source_id?: string | { id?: string; name?: string; title?: string; lead_source_id?: string } | null;
}

interface AuthContextType {
  user: User | null;
  userAssignments: UserAssignment[];
  totalCustomers: number; // Total customers available (for all_access users)
  currentAssignment: UserAssignment | null;
  /** Portal branding locked to entry hostname (strict dual-domain) */
  brand: PortalBrand;
  isTalAcquisition: boolean;
  login: (emailOrLoginId: string, password: string) => Promise<{ record: User; token: string }>;
  logout: () => void;
  setCurrentAssignment: (assignment: UserAssignment | null) => void;
  loading: boolean;
  isAuthenticated: boolean;
  checkTermsAccepted: (clientId: string, redRockId: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const isStaffAccess = (accessLevel?: UserAccessLevel | string | null): boolean =>
  accessLevel === 'support' || accessLevel === 'admin' || accessLevel === 'all_access';

const mapAssignments = (
  items: Array<{
    id: string;
    red_rock_id: string;
    red_rock_full_entity_name: string;
    user_access_type: 'can_view' | 'can_edit';
    assigned_date: string;
    lead_source?: string | null;
    lead_source_id?: UserAssignment['lead_source_id'];
  }>
): UserAssignment[] =>
  items.map((assignment) => ({
    id: assignment.id,
    red_rock_id: assignment.red_rock_id,
    red_rock_full_entity_name: assignment.red_rock_full_entity_name,
    user_access_type: assignment.user_access_type,
    assigned_date: assignment.assigned_date,
    lead_source: assignment.lead_source ?? null,
    lead_source_id: assignment.lead_source_id ?? null,
  }));

const hasLeadFields = (assignment: UserAssignment | null | undefined): boolean => {
  if (!assignment) return false;
  const source = String(assignment.lead_source ?? '').trim();
  const sourceId =
    typeof assignment.lead_source_id === 'object' && assignment.lead_source_id
      ? String(
          assignment.lead_source_id.name ??
            assignment.lead_source_id.title ??
            assignment.lead_source_id.lead_source_id ??
            assignment.lead_source_id.id ??
            ''
        ).trim()
      : String(assignment.lead_source_id ?? '').trim();
  return source.length > 0 || sourceId.length > 0;
};

/** Always load lead_source from customer_v2 when missing (expand is unreliable for branding). */
const enrichAssignmentLeadFields = async (
  assignment: UserAssignment
): Promise<UserAssignment> => {
  if (!assignment?.red_rock_id) {
    return assignment;
  }
  if (hasLeadFields(assignment)) {
    return assignment;
  }
  try {
    const leadFields = await authService.getCustomerLeadSourceFields(assignment.red_rock_id);
    return {
      ...assignment,
      lead_source: leadFields.lead_source,
      lead_source_id: leadFields.lead_source_id,
    };
  } catch (err) {
    console.error('Failed to enrich lead source for branding:', err);
    return assignment;
  }
};

const enrichAssignmentsLeadFields = async (
  assignments: UserAssignment[]
): Promise<UserAssignment[]> => {
  return Promise.all(assignments.map((a) => enrichAssignmentLeadFields(a)));
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const entryBrand = getBrandFromHostname();
  const [user, setUser] = useState<User | null>(null);
  const [userAssignments, setUserAssignments] = useState<UserAssignment[]>([]);
  const [totalCustomers, setTotalCustomers] = useState<number>(0);
  const [currentAssignment, setCurrentAssignmentState] = useState<UserAssignment | null>(null);
  const [brand, setBrand] = useState<PortalBrand>(entryBrand);
  const [loading, setLoading] = useState(true);

  const lockEntryBrand = () => {
    setBrand(entryBrand);
    persistPortalBrand(entryBrand);
    applyPortalBrandToDocument(entryBrand);
  };

  const clearSessionLocal = () => {
    setUser(null);
    setUserAssignments([]);
    setTotalCustomers(0);
    setCurrentAssignmentState(null);
    clearPersistedPortalBrand();
    lockEntryBrand();
    const tokenKey = process.env.REACT_APP_TOKEN_KEY || 'client_token';
    localStorage.removeItem(tokenKey);
  };

  /**
   * Strict domain gate.
   * Staff (support / admin / all_access): only brands in REACT_APP_STAFF_ALLOWED_BRANDS
   * (default: redrock only — changable via env).
   * Normal users: must have a customer matching this portal brand.
   */
  const assertDomainAccess = (
    accessLevel: UserAccessLevel | string | undefined,
    assignments: UserAssignment[]
  ) => {
    if (isStaffAccess(accessLevel)) {
      if (!isStaffAllowedOnBrand(entryBrand)) {
        throw new BrandMismatchError(entryBrand);
      }
      return;
    }
    if (!assignmentsMatchEntryBrand(entryBrand, assignments)) {
      throw new BrandMismatchError(entryBrand);
    }
  };

  // Keep document title / favicon in sync with entry brand
  useEffect(() => {
    applyPortalBrandToDocument(brand);
  }, [brand]);

  const setCurrentAssignment = (assignment: UserAssignment | null) => {
    void (async () => {
      if (!assignment) {
        setCurrentAssignmentState(null);
        lockEntryBrand();
        return;
      }
      const enriched = await enrichAssignmentLeadFields(assignment);
      setUserAssignments((prev) =>
        prev.map((a) =>
          a.id === enriched.id || a.red_rock_id === enriched.red_rock_id ? { ...a, ...enriched } : a
        )
      );
      setCurrentAssignmentState(enriched);
      // UI brand stays locked to the entry domain (strict)
      lockEntryBrand();
    })();
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const tokenKey = process.env.REACT_APP_TOKEN_KEY || 'client_token';
        const token = localStorage.getItem(tokenKey);
        if (!token) {
          clearPersistedPortalBrand();
          lockEntryBrand();
          return;
        }

        const userData = await authService.getCurrentUser();

        if (userData.access_level === 'support') {
          assertDomainAccess(userData.access_level, []);
          setUser(userData);
          setUserAssignments([]);
          setTotalCustomers(0);
          setCurrentAssignmentState(null);
          lockEntryBrand();
          return;
        }

        const result = await authService.getUserCustomerAssignments(
          userData.id,
          userData.access_level
        );
        let formattedAssignments = await enrichAssignmentsLeadFields(mapAssignments(result.items));

        assertDomainAccess(userData.access_level, formattedAssignments);

        // Normal users: only show customers that belong on this portal
        if (!isStaffAccess(userData.access_level)) {
          formattedAssignments = filterAssignmentsForEntryBrand(entryBrand, formattedAssignments);
        }

        let nextCurrent: UserAssignment | null = null;
        if (
          formattedAssignments.length > 0 &&
          userData.access_level !== 'all_access' &&
          userData.access_level !== 'admin'
        ) {
          nextCurrent = formattedAssignments[0];
          setCurrentAssignmentState(nextCurrent);
        }

        setUser(userData);
        setUserAssignments(formattedAssignments);
        setTotalCustomers(
          isStaffAccess(userData.access_level) ? result.totalItems : formattedAssignments.length
        );
        lockEntryBrand();
      } catch (error: any) {
        console.error('Auth check failed:', error);
        const tokenKey = process.env.REACT_APP_TOKEN_KEY || 'client_token';
        if (
          error instanceof BrandMismatchError ||
          error?.response?.status === 401 ||
          error?.message?.includes('401')
        ) {
          localStorage.removeItem(tokenKey);
          clearPersistedPortalBrand();
          lockEntryBrand();
          setUser(null);
          setUserAssignments([]);
          setTotalCustomers(0);
          setCurrentAssignmentState(null);
        }
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
    // entryBrand is derived from hostname once per page load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (emailOrLoginId: string, password: string) => {
    try {
      setLoading(true);
      const authData = await authService.login(emailOrLoginId, password);
      const tokenKey = process.env.REACT_APP_TOKEN_KEY || 'client_token';
      localStorage.setItem(tokenKey, authData.token);

      if (authData.record.access_level === 'support') {
        try {
          assertDomainAccess(authData.record.access_level, []);
        } catch (mismatchError) {
          localStorage.removeItem(tokenKey);
          clearPersistedPortalBrand();
          lockEntryBrand();
          throw mismatchError;
        }
        setUser(authData.record);
        setUserAssignments([]);
        setTotalCustomers(0);
        setCurrentAssignmentState(null);
        lockEntryBrand();
        return authData;
      }

      const result = await authService.getUserCustomerAssignments(
        authData.record.id,
        authData.record.access_level
      );
      let formattedAssignments = await enrichAssignmentsLeadFields(mapAssignments(result.items));

      try {
        assertDomainAccess(authData.record.access_level, formattedAssignments);
        if (!isStaffAccess(authData.record.access_level)) {
          formattedAssignments = filterAssignmentsForEntryBrand(entryBrand, formattedAssignments);
        }
      } catch (mismatchError) {
        // Strict: do not leave a half-logged-in session on the wrong portal
        localStorage.removeItem(tokenKey);
        clearPersistedPortalBrand();
        lockEntryBrand();
        setUser(null);
        setUserAssignments([]);
        setTotalCustomers(0);
        setCurrentAssignmentState(null);
        throw mismatchError;
      }

      let nextCurrent: UserAssignment | null = null;
      if (
        formattedAssignments.length > 0 &&
        authData.record.access_level !== 'all_access' &&
        authData.record.access_level !== 'admin'
      ) {
        nextCurrent = formattedAssignments[0];
        setCurrentAssignmentState(nextCurrent);
      }

      setUser(authData.record);
      setUserAssignments(formattedAssignments);
      setTotalCustomers(
        isStaffAccess(authData.record.access_level)
          ? result.totalItems
          : formattedAssignments.length
      );
      lockEntryBrand();

      return authData;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    clearSessionLocal();
  };

  const checkTermsAccepted = async (clientId: string, redRockId: string): Promise<boolean> => {
    try {
      // TAL Acquisition companies (TAL portal or Red Rock portal) require is_tal_acquisite rows
      const assignmentForCustomer =
        currentAssignment?.red_rock_id === redRockId
          ? currentAssignment
          : userAssignments.find((a) => a.red_rock_id === redRockId) || currentAssignment;
      const docsBrand = getLegalDocumentBrand(brand, assignmentForCustomer);
      const requireTalAcquisition =
        docsBrand === 'tal' || isTalAcquisitionCustomer(assignmentForCustomer);

      return await termsConditionsService.checkTermsAccepted(clientId, redRockId, {
        requireTalAcquisition,
      });
    } catch (error) {
      console.error('Failed to check terms acceptance:', error);
      return false;
    }
  };

  const value: AuthContextType = {
    user,
    userAssignments,
    totalCustomers,
    currentAssignment,
    brand,
    isTalAcquisition:
      brand === 'tal' || isTalAcquisitionCustomer(currentAssignment),
    login,
    logout,
    setCurrentAssignment,
    loading,
    isAuthenticated: !!user,
    checkTermsAccepted,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
